import { useState, useEffect } from "react";
import { useApp } from "../store.jsx";

function NumberRow({ label, value, onChange, suffix, step = 1, min = 0, decimals = 0, title }) {
  return (
    <div className="editor-grid" style={{ alignItems:"center" }}>
      <span className={"label " + (title ? "tt" : "")}
            style={title ? { textDecoration:"underline dotted" } : undefined}>
        {label}
        {title && <span className="tt-text">{title}</span>}
      </span>
      <input type="number" value={Number(value).toFixed(decimals)} step={step} min={min}
             onChange={e => onChange(Number(e.target.value))}/>
      <span className="val">{suffix}</span>
    </div>
  );
}

// Stacked label + wide textarea + optional hint. Self-styled (the `.field` rule
// only stacks inside `.details-editor`, which this panel isn't).
function DescField({ label, value, onCommit, hint }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  const commit = () => { if (v !== (value ?? "")) onCommit(v); };
  return (
    <div style={{ display:"grid", gap:4, marginTop:2, marginBottom:14, paddingLeft:12 }}>
      <span style={{
        color:"var(--muted)", fontSize:11, textTransform:"uppercase", letterSpacing:".04em",
      }}>{label}</span>
      <textarea rows={2} value={v}
                onChange={e => setV(e.target.value)} onBlur={commit}
                style={{
                  width:"100%", boxSizing:"border-box",
                  resize:"vertical", minHeight:48,
                  fontFamily:"inherit", fontSize:13, lineHeight:1.45,
                }}/>
      {hint && (
        <span style={{ color:"var(--muted)", fontSize:11 }}>{hint}</span>
      )}
    </div>
  );
}

export default function SettingsPanel() {
  const { settings, patchSettings, resetAll } = useApp();
  if (!settings) return <div className="card">Loading settings…</div>;

  return (
    <div className="card">
      <h3 style={{marginBottom:8}}>Global settings</h3>
      <p style={{ color:"var(--muted)", fontSize:13, marginTop:0 }}>
        These rates are applied across every task. Change them and the entire portfolio re-prices instantly.
      </p>

      <NumberRow
        label="Hourly rate"
        value={settings.hourlyRateSek}
        onChange={v => patchSettings({ hourlyRateSek: v })}
        suffix="SEK / h" step={50}
      />
      <NumberRow
        label="Hours per FTE / month"
        value={settings.hoursPerFteMonth}
        onChange={v => patchSettings({ hoursPerFteMonth: v })}
        suffix="h" step={10}
      />

      <div className="divider"/>
      <h3 style={{marginBottom:4}}>Impact score weights</h3>
      <p style={{ color:"var(--muted)", fontSize:13, marginTop:0 }}>
        Decimal factors that weight each category in the <b>average impact score</b>. Scores no longer
        contribute monetary value. Edit the description to change the tooltip shown on the category label.
      </p>

      <NumberRow
        label="Quality factor"
        value={settings.factorQuality}
        onChange={v => patchSettings({ factorQuality: v })}
        suffix="×" step={0.1} decimals={2}
        title={settings.descQuality}
      />
      <DescField
        label="Quality description"
        value={settings.descQuality}
        onCommit={v => patchSettings({ descQuality: v })}
        hint="Shown as a tooltip when hovering the Quality label."
      />

      <NumberRow
        label="Importance factor"
        value={settings.factorStrategic}
        onChange={v => patchSettings({ factorStrategic: v })}
        suffix="×" step={0.1} decimals={2}
        title={settings.descStrategic}
      />
      <DescField
        label="Importance description"
        value={settings.descStrategic}
        onCommit={v => patchSettings({ descStrategic: v })}
        hint="Shown as a tooltip when hovering the Importance label."
      />

      <NumberRow
        label="Customer factor"
        value={settings.factorCustomer}
        onChange={v => patchSettings({ factorCustomer: v })}
        suffix="×" step={0.1} decimals={2}
        title={settings.descCustomer}
      />
      <DescField
        label="Customer description"
        value={settings.descCustomer}
        onCommit={v => patchSettings({ descCustomer: v })}
        hint="Shown as a tooltip when hovering the Customer label."
      />

      <div className="divider"/>
      <div className="row-actions">
        <button className="btn danger" onClick={() => {
          if (confirm("Reset every task AND every global setting to their seeded defaults?")) resetAll();
        }}>Reset everything</button>
      </div>
    </div>
  );
}
